import AwesomeDebouncePromise from 'awesome-debounce-promise';
import classNames from 'classnames';
import saveAs from "file-saver";
import {
	CustomerApplicationFilterOptions,
	CustomerApplicationListItem,
	CustomerApplicationQueryOptions,
	fetchCustomerApplications,
} from 'Hooks/Api/apiCustomerApplications';
import { useEntityListQueryParams } from 'Hooks/Api/useEntityListQueryParams';
import { observer } from 'mobx-react';
import { customerApplicationStatus } from 'Models/Enums';
import { customerApplicationStatusComboboxOptions, customerApplicationStatusOptions } from 'Models/Enums/MappedEnumOptions';
import { store } from 'Models/Store';
import moment from 'moment';
import { RouteComponentProps } from 'react-router-dom';
import { DEFAULT_PAGE_LENGTH } from 'Util/Constants';
import { buildBlob } from 'Util/CsvUtils';
import { PageBreadcrumbs } from 'Views/Components/Breadcrumbs/PageBreadcrumbs';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import ButtonAsyncState from 'Views/Components/Button/ButtonAsyncState';
import MultiComboboxSetter from 'Views/Components/Combobox/MultiComboboxSetter';
import CustomerApplicationStatusBox from 'Views/Components/CustomerApplications/CustomerApplicationStatusBox';
import { DateRangePicker } from 'Views/Components/DateRangePicker/DateRangePicker';
import EntityList, { IEntityListHeaderProps } from 'Views/Components/EntityList/EntityList';
import MeatballMenu, { MeatballMenuAction } from 'Views/Components/EntityList/MeatballMenu';
import { CountWithIcon } from 'Views/Components/Intel/CountWithIcon';
import TablePagination from 'Views/Components/Pagination/TablePagination';
import RequestWrap from 'Views/Components/RequestWrap/RequestWrap';
import AccessIntelSecuredPage from 'Views/Components/Security/AccessIntelSecuredPage';
import InlineSpinner from 'Views/Components/Spinner/InlineSpinner';
import { TextFieldSetter } from 'Views/Components/TextBox/TextFieldSetter';
import { useCustomerApplicationPageFilters } from './useCustomerApplicationPageFilters';
import { EntityControllerData } from 'Hooks/Api/shared';
import { Row } from 'Views/Components/FlexComponents/Row';
import { CustomerApplicationAuthorisation, useCustomerApplicationAuthorisation } from './useCustomerApplicationAuthorisation';

export async function mapDataToExport(data: EntityControllerData<CustomerApplicationListItem>, userHasElevatedAccess: boolean) {

	const result = [] as string[][];
	const columns: { displayName: string, value: (item: CustomerApplicationListItem) => string }[] = [
		{
			displayName: "Application ID",
			value: item => item.applicationId ? item.applicationId : '-',
		},
		{
			displayName: "Created Date",
			value: item => item.created ? moment(item.created).format('DD/MM/YYYY') : '-'
		},
		{
			displayName: "Email Sent",
			value: item => item.emailSent ? "Yes" : "No"
		},
		{
			displayName: "Business Name",
			value: item => item.businessName ? item.businessName : '-'
		},
		{
			displayName: "Email",
			value: item => item.email ? item.email : '-'
		},
		{
			displayName: "Status",
			value: item => item.status ? customerApplicationStatusOptions[item.status] : '-',
		}
	]

	if (userHasElevatedAccess) {
		columns.push(
			{
				displayName: "Credit Applied",
				value: item => (item.creditRequested ?? 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" }),
			},
			{
				displayName: "Credit Approved",
				value: item => (item.creditApproved ?? 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" }),
			},
			{
				displayName: "Submitted Date",
				value: item => item.submitted ? moment(item.submitted).format('HH:mm DD/MM/YYYY') : '-'
			},
			{
				displayName: "Account Number",
				value: item => item.accountNumber ? item.accountNumber : '-'
			},
			{
				displayName: "Alerts",
				value: item => item.alerts ? item.alerts.toString() : '-',
			},
			{
				displayName: "Reports",
				value: item => item.reports ? item.reports.toString() : '-',
			});
	}


	const headers = columns.map(column => column.displayName?.toString() ?? '');
	result.push(headers);
	data.data.forEach(searchResult => {
		result.push(
			columns.map(column => column.value(searchResult)?.toString() ?? ''),
		);
	});

	return result;
};



const fetchCustomerApplicationsDebounced = AwesomeDebouncePromise(
	async (queryParams: CustomerApplicationQueryOptions) => await fetchCustomerApplications(queryParams),
	400,
	{ leading: true }
);

/**
 * Returns list of action items for individual customer application item
 */
export function getCustomerApplicationActionItems(
	item: CustomerApplicationListItem,
	approveBaseUrl: string,
	auth: CustomerApplicationAuthorisation,
): MeatballMenuAction[] {

	return [
		{
			display: 'Edit Application',
			action: () => window.open(`/approve/customerapplications/${item.id}/edit`, '_blank'),
			visible: auth.canEdit,
			className: 'meatball-item'
		},
		{
			display: 'Review Application (Super User)',
			action: () => {
				// Hide greenfield version until its ready
				const url = `/approve/customerapplications/${item.id}`;
				window.open(url, '_blank')
			},
			visible: store.getUser?.userType === 'SUPER_USER',
			className: 'meatball-item'
		},
		{
			display: 'Review Application',
			action: () => {
				// Hide greenfield version until its ready
				const url = `${approveBaseUrl}/index.php?page=Application_Approval.php&APP_CR=${item.applicationId}`;
				window.open(url, '_blank')
			},
			visible: auth.canOtherAction,
			className: 'meatball-item'
		},
		{
			display: 'Process Decision',
			action: () => window.open(`${approveBaseUrl}/index.php?page=Application_Approval.php&APP_CR=${item.applicationId}`, '_blank'),
			visible: auth.canOtherAction,
			className: 'meatball-item'
		},
		{
			display: 'View Reports',
			action: () => window.open(`${approveBaseUrl}/index.php?page=Applications_CR_01.php&APP_CR=${item.applicationId}`, '_blank'),
			visible: auth.canOtherAction,
			className: 'meatball-item'
		},
		{
			display: 'View Documents',
			action: () => window.open(`${approveBaseUrl}/index.php?page=Display_4.php&APP_CR=${item.applicationId}`, '_blank'),
			visible: auth.canOtherAction,
			className: 'meatball-item'
		},
		{
			display: 'Reset Password',
			action: () => window.open(`${approveBaseUrl}/index.php?page=Applications_Password_Reset.php&APP_CR=${item.applicationId}`, '_blank'),
			visible: auth.canOtherAction,
			className: 'meatball-item'
		},
		{
			display: 'Email Correspondence',
			action: () => window.open(`${approveBaseUrl}/index.php?page=Manager_Emails_Sent.php&Button_Selected=Management&From_Menu=Y&APP_CR=${item.applicationId}`, '_blank'),
			visible: auth.canOtherAction,
			className: 'meatball-item'
		},
		{
			display: 'Delete Application',
			action: () => window.open(`${approveBaseUrl}/index.php?page=Applications_Delete.php&APP_CR=${item.applicationId}`, '_blank'),
			visible: auth.canDelete,
			className: 'meatball-item delete'
		},
	];
}


const Tags = [
	{ label: 'Customer Applications' },
];

export interface CustomerApplicationPageProps { }

const CustomerApplicationPage = observer((props: RouteComponentProps<CustomerApplicationPageProps>) => {

	const {
		filters,
		setFilters,
		resetFilters,
		showFilters,
		setShowFilters,
		getUserOptions,
		getCustomerApplicationFormOptions,
		getCustomerApplicationTemplateTagOptions,
		fetchingOptionsErrors
	} = useCustomerApplicationPageFilters();

	const {
		searchBy,
		sortBy,
		sortDescending,
		pageNo,
		pageSize,
		setSearchBy,
		setSortBy,
		setPageNo,
		changedSignature: refreshKey,
		reset: resetQueryParams,
	} = useEntityListQueryParams({
		sortBy: "applicationId",
		sortDescending: true,
	})

	const customerAppAuth = useCustomerApplicationAuthorisation(store.getUser);

	const fetchUsersDebounced = AwesomeDebouncePromise(getUserOptions, 400, { leading: true });

	const updateFilters = (newFilters: Partial<CustomerApplicationFilterOptions>) => {
		if (!newFilters || !filters) return;
		setFilters({ ...filters, ...newFilters });
		setPageNo(0); // Reset to first page when filters change
	};

	const onStatusFilterChange = (data: (customerApplicationStatus | 'ALL')[]) => {
		const newValue = data;
		if (!newValue || newValue.length === 0) {
			updateFilters({ filterByStatus: ['ALL'] });
			return;
		}
		const isAllSelected = newValue.includes('ALL');
		const hasAllPreviously = filters?.filterByStatus?.includes('ALL');
		if (isAllSelected && !hasAllPreviously) {
			updateFilters({ filterByStatus: ['ALL'] });
		}
		else if (isAllSelected && hasAllPreviously) {
			updateFilters({ filterByStatus: newValue.filter(status => status !== 'ALL') });
		} else if (newValue.length === customerApplicationStatusComboboxOptions.length - 1) {
			updateFilters({ filterByStatus: ['ALL'] });
		} else {
			updateFilters({ filterByStatus: newValue });
		}
	}

	const getColumns = (): IEntityListHeaderProps<CustomerApplicationListItem>[] => {
		const columns: IEntityListHeaderProps<CustomerApplicationListItem>[] = [
			{
				columnName: "id",
				displayName: "ID",
				value: item => item.id,
				hide: true
			},
			{
				columnName: "applicationId",
				displayName: "Application ID",
				value: item => item.applicationId,
				sortable: true,
				sortClicked: () => setSortBy('applicationId')
			},
			{
				columnName: "createdDate",
				displayName: "Created Date",
				value: item => item.created && moment(item.created).format('h:mm A D/M/YYYY'),
				sortable: true,
				sortClicked: () => setSortBy('createdDate'),
			},
			{
				columnName: "emailSent",
				displayName: "Form Sent",
				value: item => <span className={classNames('entity-status-icon', {
					valid: item.emailSent,
					invalid: !item.emailSent,
				})} />,
				className: "col-email-sent",
				sortable: true,
				sortClicked: () => setSortBy('emailSent')
			},
			{
				columnName: "businessName",
				displayName: "Business Name",
				value: item => item.businessName,
			},
			{
				columnName: "email",
				displayName: "Email",
				value: item => item.email,
				sortable: true,
				sortClicked: () => setSortBy('email')
			},
			{
				columnName: "status",
				displayName: "Status",
				value: item => item.status && <CustomerApplicationStatusBox status={item.status} />,
				sortable: true,
				sortClicked: () => setSortBy('status')
			},
			{
				columnName: "alerts",
				displayName: "Alerts",
				value: item => item.alerts ? CountWithIcon({ count: item.alerts, icon: "warning-2" }) : '',
				sortable: true,
				hide: !customerAppAuth.elevatedColumnPermissions,
				sortClicked: () => setSortBy('alerts')
			},
			{
				columnName: "reports",
				displayName: "Reports",
				value: item => item.reports ? CountWithIcon({ count: item.reports, icon: "download" }) : '',
				sortable: true,
				hide: !customerAppAuth.elevatedColumnPermissions,

				sortClicked: () => setSortBy('reports')
			},
			{
				columnName: "creditRequested",
				displayName: "Credit Applied",
				value: item => (item.creditRequested ?? 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" }),
				sortable: true,
				hide: !customerAppAuth.elevatedColumnPermissions,
				sortClicked: () => setSortBy('creditRequested')
			},
			{
				columnName: "submittedDate",
				displayName: "Submitted Date",
				value: item => item.submitted && moment(item.submitted).format('h:mm A D/M/YYYY'),
				sortable: true,
				hide: !customerAppAuth.elevatedColumnPermissions,
				sortClicked: () => setSortBy('submittedDate')
			},
			{
				columnName: "accountNumber",
				displayName: "Account Number",
				value: item => item.accountNumber,
				sortable: true,
				hide: !customerAppAuth.elevatedColumnPermissions,
				sortClicked: () => setSortBy('accountNumber')
			},
			{
				columnName: "creditApproved",
				displayName: "Credit Approved",
				value: item => (item.creditApproved ?? 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" }),
				sortable: true,
				hide: !customerAppAuth.elevatedColumnPermissions,
				sortClicked: () => setSortBy('creditApproved')
			},
			{
				columnName: 'meatball-menu',
				displayName: 'Actions',
				value: item => <MeatballMenu actions={getCustomerApplicationActionItems(item, store.appSettings.approveBaseUrl, customerAppAuth)} />,
				sortable: false,
				className: 'meatball-menu',
			}
		];

		return columns;
	}

	const handleExport = async () => {
		const exportQueryOptions = {
			...fetchCustomerApplicationQueryOptions,
			pageSize: undefined
		}
		const exportedApplications = await fetchCustomerApplications(exportQueryOptions)
		const data = await mapDataToExport(exportedApplications.data, customerAppAuth.elevatedColumnPermissions);
		const blob = buildBlob(data, ',', '"');
		saveAs(blob, `exported-customer-applications-${moment().format('YYYY-MM-DD')}.csv`);
	};

	const toggleFilter = () => {
		setShowFilters(prev => {
			// Reset filters only if  going from show to hide
			if (prev === true) {
				resetQueryParams();
				resetFilters();
			}
			return !prev
		});
	}

	const fetchCustomerApplicationQueryOptions: CustomerApplicationQueryOptions = {
		searchBy: searchBy,
		searchOn: !!searchBy ? ['email', 'abn', 'acn', 'businessName', 'applicationId', 'nzbn', 'ncn'] : undefined,
		filterByCustomerApplicationFormIds: filters?.filterByCustomerApplicationFormIds,
		filterByStatus: filters?.filterByStatus?.includes('ALL') ? [] : filters?.filterByStatus,
		filterByCreatedDateRange: filters?.filterByCreatedDateRange,
		filterBySubmittedDateRange: filters?.filterBySubmittedDateRange,
		filterByDecisionDateRange: filters?.filterByDecisionDateRange,
		filterByUserIds: filters?.filterByUserIds,
		filterByCustomerApplicationTagIds: filters?.filterByCustomerApplicationTagIds,
		pageNo,
		pageSize,
		sortBy,
		sortDescending,
	}

	const onClickRow = (item: CustomerApplicationListItem) => {
		// MVP Review Application
		if (store.userType === 'SALESPERSON') {
			store.routerHistory.push(`/approve/customerapplications/${item.id}/edit`);
			return;
			// Future: When the page is ready, let other users see this.
		} else if (store.userType === 'SUPER_USER') {
			store.routerHistory.push(`/approve/customerapplications/${item.id}`);
			return;
			// Every other user goes to review summary
		} else {
			window.location.href = `${store.appSettings.approveBaseUrl}/index.php?page=Display_Summary.php&APP_CR=${item.applicationId}`;
		}
	}

	return (
		<AccessIntelSecuredPage routeComponentProps={props} product="approve">
			<div className="body-content list-page customer-applications-list">
				<PageBreadcrumbs tags={Tags} />
				<div className="search-container">
					<div className="searchbar search">
						<TextFieldSetter
							className={classNames('search-input search__collection')}
							value={searchBy}
							setValue={setSearchBy}
							onAfterChange={() => setPageNo(0)}
							label=""
							labelVisible={false}
							placeholder="Search by Email/Business Entity Numbers/Applicant name/Business Name/Application ID"
						/>
						<Button
							className={classNames('filter-toggle-btn', { active: showFilters })}
							icon={{ icon: 'filter-2', iconPos: 'icon-left' }}
							colors={Colors.Primary}
							display={showFilters ? Display.Solid : Display.Outline}
							onClick={toggleFilter}
						>
							{showFilters ? 'Clear Filter' : 'Filter By'}
						</Button>
					</div>

					<div className="actions">
						<Button
							className="new-customer-btn"
							icon={{ icon: 'plus-2', iconPos: 'icon-left' }}
							colors={Colors.Primary}
							display={Display.Solid}
							onClick={() => store.routerHistory.push("/approve/customerapplications/add")}
						>
							Create customer
						</Button>
						<ButtonAsyncState
							className="export-button"
							colors={Colors.Alternate}
							display={Display.Outline}
							onPress={handleExport}
							waitingText='Exporting...'
						>
							Export CSV
						</ButtonAsyncState>
					</div>
					{showFilters && (
						<div className="search-filter-section customer-application-search-filter-section">
							<Row>
								<DateRangePicker
									className="date-range-picker"
									model={filters?.filterByCreatedDateRange ?? []}
									modelProperty="value"
									placeholder="Filter by created date range"
									flatpickrOptions={{
										formatDate: (date: Date) => moment(date).format('DD/MM/YYYY'),
										maxDate: moment().format('YYYY-MM-DD'),
									}}
									dateFormat="DD/MM/YYYY"
									onAfterChange={newValue => {
										const parsedDates = newValue.map(str => moment(str).format('YYYY-MM-DD'));
										updateFilters({ filterByCreatedDateRange: parsedDates });
									}}
								>
								</DateRangePicker>
								{/* Tech debt: click to clear prop on DateRangePicker not working */}
								<button
									className="click-to-clear icon-cross icon-left"
									onClick={() => { updateFilters({ filterByCreatedDateRange: [] }) }}
									type="button"
									aria-label="Click to clear"
								/>
							</Row>
							<Row>
								<DateRangePicker
									className="date-range-picker"
									model={filters?.filterBySubmittedDateRange ?? []}
									modelProperty="value"
									placeholder="Filter by applicant submitted date range"
									flatpickrOptions={{
										formatDate: (date: Date) => moment(date).format('DD/MM/YYYY'),
										maxDate: moment().format('YYYY-MM-DD'),
									}}
									dateFormat="DD/MM/YYYY"
									onAfterChange={newValue => {
										const parsedDates = newValue.map(str => moment(str).format('YYYY-MM-DD'));
										updateFilters({ filterBySubmittedDateRange: parsedDates });
									}}
								>
								</DateRangePicker>
								{/* Tech debt: click to clear prop on DateRangePicker not working */}
								<button
									className="click-to-clear icon-cross icon-left"
									onClick={() => { updateFilters({ filterBySubmittedDateRange: [] }) }}
									type="button"
									aria-label="Click to clear"
								/>
							</Row>
							<Row>
								<DateRangePicker
									className="date-range-picker"
									model={filters?.filterByDecisionDateRange ?? []}
									modelProperty="value"
									placeholder="Filter by application decision date range"
									flatpickrOptions={{
										formatDate: (date: Date) => moment(date).format('DD/MM/YYYY'),
										maxDate: moment().format('YYYY-MM-DD'),
									}}
									dateFormat="DD/MM/YYYY"
									onAfterChange={newValue => {
										const parsedDates = newValue.map(str => moment(str).format('YYYY-MM-DD'));
										updateFilters({ filterByDecisionDateRange: parsedDates });
									}}
								>
								</DateRangePicker>
								{/* Tech debt: click to clear prop on DateRangePicker not working */}
								<button
									className="click-to-clear icon-cross icon-left"
									onClick={() => { updateFilters({ filterByDecisionDateRange: [] }) }}
									type="button"
									aria-label="Click to clear"
								/>
							</Row>
							<MultiComboboxSetter
								label='Filter by status'
								className="status-filter"
								getOptionValue={(value?: string) => value}
								placeholder="Filter by status"
								labelVisible={false}
								searchable
								options={customerApplicationStatusComboboxOptions}
								value={filters?.filterByStatus ?? ['ALL']}
								onChange={(_, data) => onStatusFilterChange(data.value as (customerApplicationStatus | 'ALL')[])}
							/>
							<MultiComboboxSetter
								value={filters?.filterByUserIds ?? []}
								onChange={(_, data) => updateFilters({ filterByUserIds: data.value as string[] })}
								getOptionValue={(value?: string) => value}
								placeholder="Sales rep"
								label=""
								labelVisible={false}
								searchable
								isClearable
								options={fetchUsersDebounced}
								initialOptions={getUserOptions}
							/>
							<MultiComboboxSetter
								label=""
								placeholder="Customer Application Form"
								value={filters!.filterByCustomerApplicationFormIds ?? []}
								getOptionValue={(value?: string) => value}
								initialOptions={getCustomerApplicationFormOptions}
								options={getCustomerApplicationFormOptions}
								searchable
								isClearable
								onChange={(_, data) => updateFilters({ filterByCustomerApplicationFormIds: data.value as string[] })}
								errors={fetchingOptionsErrors.customerApplicationTemplates}
							/>
							<MultiComboboxSetter
								label=""
								placeholder="Customer Application Groups"
								value={filters!.filterByCustomerApplicationTagIds ?? []}
								getOptionValue={(value?: string) => value}
								initialOptions={getCustomerApplicationTemplateTagOptions}
								options={getCustomerApplicationTemplateTagOptions}
								isClearable
								onChange={(_, data) => updateFilters({ filterByCustomerApplicationTagIds: data.value as string[] })}
								errors={fetchingOptionsErrors.customerApplicationTemplateTags}
							/>
						</div>
					)}
				</div>
				<RequestWrap
					request={() => fetchCustomerApplicationsDebounced(fetchCustomerApplicationQueryOptions)}
					refreshKey={store.impersonatingOrganisationId + JSON.stringify({ ...filters, refreshKey })}
					loadingContent={<InlineSpinner />}
					requestOptions={{
						suppressSubsequentLoadingState: false
					}}
				>
					{request => {
						const records = request.data.data ?? [];
						return (
							<div className="collection-component">
								<EntityList
									onClickRow={onClickRow}
									collection={records}
									columns={getColumns()}
									idColumn="applicationId"
									sortColumn={sortBy ?? 'applicationId'}
									sortDescending={sortDescending ?? false}
								/>
								<section className="collection__load">
									<TablePagination
										perPage={pageSize || DEFAULT_PAGE_LENGTH}
										pageNo={pageNo ?? 0}
										totalRecords={request.data.count}
										onPageChange={setPageNo}
									/>
								</section>
							</div>
						)
					}}
				</RequestWrap>
			</div>
		</AccessIntelSecuredPage>
	);
});
export default CustomerApplicationPage;
